import { createContext, ReactNode, useContext, useMemo } from 'react';
import { find, get, isNil, map } from 'lodash';
import {
  useDiscoveryIdSelector,
  useOpenDiscoveryPresentStateSelector,
  useOpenVizSelector,
  useVizOptionSelector,
} from '../../common/redux/selectors/viz-selector.hook';
import { IFieldsWidgetContext } from './fields-widget.interfaces';
import { VIZ_OPTION_IDS } from '../../common';
import { Viz } from '../VizUtil';

type Props = {
  children: ReactNode;
  legendGroupDataId: string;
};

export const FieldsWidgetContext = createContext<IFieldsWidgetContext>({
  data: {
    visualizations: [],
  },
  selectedTypes: {
    fields: [],
  },
});

export const FieldsWidgetContextProvider = ({
  children,
  legendGroupDataId,
}: Props) => {
  const discoveryId = useDiscoveryIdSelector();
  const viz = useOpenVizSelector({
    discoveryId,
  });
  const { legendData: legendDataList = [] } =
    useOpenDiscoveryPresentStateSelector({
      discoveryId,
    });
  const legendGroupData = useVizOptionSelector({
    discoveryId,
    option: VIZ_OPTION_IDS.legendGroupMetadata,
    defaultValue: [],
  });
  const legendData = legendDataList.reduce((acc, item) => {
    if (!isNil(item.label)) {
      acc.push(item);
    }
    return acc;
  }, []);

  const { fields, selectedFields } = useMemo(() => {
    const allFields = Viz.getAllAvailableFields(viz);
    const fieldDictionary = Viz.createFieldDictionary('name', allFields);
    const getDisplayName = (name: string) => {
      return get(fieldDictionary, [name, 'displayName'], name);
    };
    const fields = map(legendData, ({ label }) => getDisplayName(label));
    const selectedFieldNames = get(
      find(legendGroupData, { id: legendGroupDataId }),
      'fields',
      [],
    );
    const selectedFields = map(selectedFieldNames, getDisplayName);
    return { fields, selectedFields };
  }, [legendData, legendGroupData, legendGroupDataId, viz]);

  return (
    <FieldsWidgetContext.Provider
      value={{
        data: {
          visualizations: [
            {
              fields,
            },
          ],
        },
        selectedTypes: {
          fields: selectedFields,
        },
      }}
    >
      {children}
    </FieldsWidgetContext.Provider>
  );
};

export const useFieldsWidgetContext = () => useContext(FieldsWidgetContext);
